import { domReady, initOnIntersection, triggerEvent } from 'utilities/dom';
import { initStickyHeaderSearch } from 'features/stickyHeaderSearch';
import { trackSortMoreYouMayLike, trackSortH1, utagLinkSort } from 'features/tealium';
import { prefetchWebpackAssetSet } from 'features/webpack';
import { recentlyViewedGAEvents } from 'features/recently-viewed';
import { setGenericEvent, setSearch } from 'features/tealium/ga4/custom';
import LPGlider from 'ui/glider';
import unveil from 'ui/lazy-load/unveil';
import { initBreadcrumbTracking, initShowMoreBreadcrumbItems } from 'features/breadcrumbs';

import dynamicImport from '../../../features/webpack/dynamicImport.macro';
import { initializeTrimHybridContent } from '../../../../Scripts/hybridContent/index';

import addRetryImage from './addRetryImage';
import initAddSchemeToWishList from './addSchemeToWishList';
import initAvailableInStore from './availableInStore';
import initBackToTop from './backToTop';
import initCaptureProductImpressions from './captureProductImpressions';
import initClearance from './clearance';
import initDataCapture from './dataCapture';
import initFilterDisplaySet from './filterDisplaySet';
import initInspireHomeDecor from './inspireHomeDecor';
import initLandingDescription from './landingDescription';
import initMoreLikeThis from './moreLikeThis';
import initNewTrendingPage from './newTrending';
import initOpenBox from './openBox';
import initShopAll from './shopAll';
import initSplashContent from './splashContent';
import initTrackProductClick from './trackProductClick';
import initTrendJenniferFerrellPage from './trendJenniferFerrell';
import initCouponModal from './couponModal';
import initSearchMoreLikeThisEvent from './trackSearchPageMoreLikeThis';
import initSearchSale from './saleSearch';
import initImageHoverEffect from './imageHoverEffect';
import initContextualSearchBar from './contextualSearchBar';
import initWishList from './wishlist';
import initFreeShippingTooltip from './freeShippingTooltip';
import initSortBy from './sortBy';
import updatePage from './_dynamic';

let pageCleanups = [];

function collectCleanups (cleanups) {

	if (cleanups?.length) {

		pageCleanups = [ ...pageCleanups, ...cleanups ];

	}

}

function initializePage () {

	pageCleanups = [];
	const { globals, certona } = window.lp;
	const { canShowCertona, isKiosk } = globals;
	const {
		inspireHomeDecorPage,
		isNewTrendingPage,
		showBreadCrumbOnZeroResults,
		topLevelCategory,
		trendJenniferFerrellPage,
		couponCode,
		isCouponCodeValid
	} = window.lp.pageData;

	// No results page
	const noResultsDiv = document.getElementById('noResults');
	if (noResultsDiv) {

		if (showBreadCrumbOnZeroResults) {

			initBreadCrumb();
			initAvailableInStore();

		}

		// Don't init results page business
		return;

	}

	// Results page

	// Add Selected Class to the Top Level Category
	const topLevelCategoryNavElement = document.getElementById(topLevelCategory);
	if (topLevelCategoryNavElement && topLevelCategory.length > 0) {

		topLevelCategoryNavElement.classList.add('selected');

	}

	const sortResultContainers = document.querySelectorAll('.sortResultContainer');
	const sortSkuViews = document.querySelectorAll('.sortResultProdImg[data-sku]');

	collectCleanups(unveil('#sortResultProducts img.unveil[data-src]'));

	// PLA pages have their own Recently Viewed widget bindings
	if (!window.lp._ProductListingAd) {

		// bind GA events when Recently Viewed Widget is ready
		const handleRecommendedItemsLoaded = () => {

			recentlyViewedGAEvents(utagLinkSort());
			trackSortMoreYouMayLike();

		};
		document.addEventListener('recommendedItems:loaded', handleRecommendedItemsLoaded, { once: true });
		collectCleanups([
			() => {

				document.removeEventListener('recommendedItems:loaded', handleRecommendedItemsLoaded);

			}
		]);

	}

	// Initialize Sort Hover Image Effect on Chandeliers (for now)
	if (window.location.pathname.startsWith('/products/chandeliers/')) {

		const sortHoverImages = document.querySelectorAll('.sortResultProdImg[data-hover-src]');
		initImageHoverEffect(sortHoverImages);

	}

	initBreadcrumbTracking();
	initShowMoreBreadcrumbItems();

	initSplashContent();

	// Coupon information in a modal popup
	initCouponModal(couponCode, isCouponCodeValid);

	// TODO: Prefix with if open box?
	initOpenBox();

	initSearchSale();

	initContextualSearchBar();

	initWishList();

	collectCleanups(initializeTrimHybridContent('sortPagehybridContent'));

	collectCleanups(initFreeShippingTooltip());

	const addScheme = document.querySelector('.addScheme');
	if (addScheme) {

		// TODO: Maybe dynamic import for hospitality user only
		initAddSchemeToWishList(addScheme);

	}
	// Data Capture
	collectCleanups(initDataCapture(sortResultContainers, sortSkuViews));
	// init product impressions capturing for utag
	collectCleanups(initCaptureProductImpressions(sortSkuViews));

	// assign function to window for Dynamic Yield to use if it changes data after render
	window.initFilterDisplaySet = initFilterDisplaySet;
	collectCleanups(initFilterDisplaySet());
	initSortBy();
	collectCleanups(initStickyHeaderSearch());

	// Sort Items
	addRetryImage('.sortResultImgContainer img');
	initTrackProductClick(sortResultContainers);

	initMoreLikeThis();

	initShopAll();

	initLandingDescription();

	initBackToTop();

	initSearchMoreLikeThisEvent();

	// certona
	if (canShowCertona) {

		const performReset = true;
		certona.getRecommendedItems([], performReset);

	}

	// Sort list type, sort position, and sort sku tracking for PDP via recommendedItems
	// clear storageSession
	const $certonaItems = $('#certonaItems, #recentlyViewedContainer');
	$certonaItems.click(() => {

		sessionStorage.setItem('listType', '');
		sessionStorage.setItem('sortPosition', '');
		sessionStorage.setItem('sortSku', '');

	});

	const pixleeElement = document.getElementById('shopByRoomPixleeWidget');
	const marker = document.getElementById('pixleeWidgetScrollMarker');

	if (pixleeElement && globals) {

		const pixleeWidgetShowNextPrevButtonShopByRoomPageToggleOn = JSON.parse(
			globals.pixleeWidgetShowNextPrevButtonShopByRoomPageToggleOn
		);
		collectCleanups(
			initOnIntersection(
				marker,
				() => {

					dynamicImport(
						'desktop/sort-shopByRoom-pixleeInstagramFeed',
						'./shopByRoomPixleeInstagramFeed'
					).then((module) => {

						const initShopByRoomInstagramFeed = module.default;
						initShopByRoomInstagramFeed(
							pixleeElement,
							pixleeWidgetShowNextPrevButtonShopByRoomPageToggleOn
						);

					});

				},
				'0px 0px 500px 0px'
			)
		);

	}
	const scrollButton = document.querySelector('#sortScrollButton');

	// animated scroll
	const sortScrollTo = (target) => {

		const element = document.getElementById(target);
		element.scrollIntoView({ behavior: 'smooth' });

	};

	if (scrollButton) {

		scrollButton.addEventListener('click', (e) => {

			e.preventDefault();
			sortScrollTo('sortFilterWrapper');

		});

	}

	prefetchWebpackAssetSet('desktop/product');

	if (isKiosk) {

		dynamicImport('desktop/sort-availableInStore', './availableInStore').then((module) => module.default());

	}

	// Track H1 values
	trackSortH1();

	const parallaxContainers = document.querySelectorAll('.lpParallaxContainer[data-parallax]');

	// parallax
	if (parallaxContainers.length) {

		dynamicImport('desktop/initParallax', './initParallax/index.js').then((module) => {

			const initParallax = module.default;
			collectCleanups(initParallax());

		});

	}

	// GA4 custom event for search
	if (window.utag_data && window.utag_data.page_type === 'searchresults') {

		setSearch({
			search_keyword: window.utag_data?.search_keyword ?? '',
			search_provider: window.utag_data?.search_provider ?? '',
			search_results: window.utag_data?.search_results ?? '0'
		});

	}

	const fromProductFinderChandelier = document.referrer.indexOf('/product-finder/chandeliers/') !== -1;

	if (fromProductFinderChandelier) {

		setGenericEvent({
			event_name: 'finder_chandeliers_results_load'
		});

	}

	const sortGlider = document.querySelector('.sortGlider__container');

	if (sortGlider) {

		const SLIDES_TO_SHOW = 5;
		const gliderWrapperSelector = '.sortGlider';
		const gliderOptions = {
			slidesToShow: SLIDES_TO_SHOW,
			slidesToScroll: SLIDES_TO_SHOW,
			dots: false,
			rewind: false,
			duration: 0.3,
			arrows: {
				prev: `${gliderWrapperSelector} .glider-prev`,
				next: `${gliderWrapperSelector} .glider-next`
			}
		};

		document.querySelector(gliderWrapperSelector).classList.remove('hidden');

		const handleGliderLoaded = function () {

			const glider = LPGlider(this);
			glider.setActiveSlide(0);
			this.classList.remove('glider-loading');

		};
		sortGlider.addEventListener('glider-loaded', handleGliderLoaded, { once: true });

		const sortGliderInstance = new LPGlider(sortGlider, gliderOptions);

		collectCleanups([
			function () {

				if (sortGliderInstance) {

					// Sometimes glider destroy expects an element that no longer exists
					// Related to pace of load/unload
					try {

						// Necessary to call destroy to cleanup window resize handlers
						sortGliderInstance.destroy();

					} catch (e) {}

				}

				if (sortGlider && handleGliderLoaded) {

					sortGlider.removeEventListener('glider-loaded', handleGliderLoaded);

				}

			}
		]);

	}

	collectCleanups(initClearance());

	if (inspireHomeDecorPage) {

		collectCleanups(initInspireHomeDecor());

	}

	if (trendJenniferFerrellPage) {

		initTrendJenniferFerrellPage();

	}

	if (isNewTrendingPage) {

		collectCleanups(initNewTrendingPage());

	}

	// Cleanup orphaned img and iframe
	const cleanupTrackingOrphans = () => {

		const selectors = [
			'img[id^="batBeacon"]', // Bing
			'iframe[title="Criteo DIS iframe"]', // Criteo
			'iframe[data-tagging-id]', // GTM
			'img[src^="https://s-vop.sundaysky"]' // SundaySky
		];
		const orphans = document.querySelectorAll(selectors.join(','));
		orphans.forEach((orphan) => orphan.remove());

	};
	collectCleanups([ cleanupTrackingOrphans ]);

}

function isSortUrl (url) {

	const lpProductsPath = /^(https\:\/\/www\.lampsplus\.com\/|https\:\/\/localhost\:44300\/|\/)products\//i;
	const lpProductPageRegex = /\.html$/i;
	const leftOfQsp = url.split('?')[0];
	return lpProductsPath.test(leftOfQsp) && !lpProductPageRegex.test(leftOfQsp);

}

function isValidDynamicDestination (url) {

	// Statically load SFP and non-sort URLs incidentally sent for dynamic navigation
	// SFP: Quicklook and Product JS does not support dynamic navigation
	return isSortUrl(url) && !url.includes('sfp');

}

// Handle dynamic navigation
async function handleDynamicNavigation (event) {

	const { destinationUrl } = event.detail;

	if (!isValidDynamicDestination(destinationUrl)) {

		window.location.href = destinationUrl;
		return;

	}

	// didUpdate: possible values: undefined from rapid navigation early return, true|false based on execution success
	const didUpdate = await updatePage(destinationUrl, pageCleanups);
	if (didUpdate) {

		initializePage();
		window.history.pushState(null, '', destinationUrl);
		triggerEvent.call(document, 'dynamicNavigationReady');

	} else if (didUpdate === false) {

		window.location.href = destinationUrl;

	}

}

// Handle back/forward
async function handlePopState (event) {

	const destinationUrl = window.location.href;

	if (!isValidDynamicDestination(destinationUrl)) {

		window.location.href = destinationUrl;
		return;

	}

	const didUpdate = await updatePage(destinationUrl, pageCleanups);
	if (didUpdate) {

		initializePage();
		triggerEvent.call(document, 'dynamicNavigationReady');

	} else if (didUpdate === false) {

		window.location.href = destinationUrl;

	}

}

domReady(() => {

	document.addEventListener('dynamicNavigation', handleDynamicNavigation);
	window.addEventListener('popstate', handlePopState);

	initializePage();

});
